import type { MeProfile } from '@/api/services/me';
import type { FeatureFlagKey } from '@/api/services/featureFlags';
import {
  canBrowseTemplates,
  canViewCustomerBillingNav,
  canViewQuickSetup,
  canViewRecipientsNav,
  canViewSubscriptions,
  canViewPlatformActivity,
  canViewWorkspaceActivity,
  canViewWorkspaceUsage,
  canViewWorkspaceUsers,
} from '@/utils/navCapabilities';
import { canViewWorkspaceNav } from '@/utils/workspaceNav';
import {
  IconActivity,
  IconDashboard,
  IconGlobe,
  IconJobs,
  IconLink,
  IconSearch,
  IconSettings,
  IconUsers,
} from './Icons';

export type FeatureFlagsMap = Partial<Record<FeatureFlagKey, boolean>>;

export type NavContext = 'platform' | 'workspace' | 'account';

/** @deprecated Use NavContext */
export type NavSection = 'administration' | 'workspace';

export type NavItem = {
  id: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  href: string;
  context: NavContext;
  capability?: string;
  end?: boolean;
  highlight?: boolean;
  featureFlag?: FeatureFlagKey;
  visible?: (me: MeProfile | null, flags: FeatureFlagsMap) => boolean;
  /** @deprecated Use href */
  to?: string;
  /** @deprecated Use context === 'platform' */
  section?: NavSection;
};

export type NavGroup = {
  id: string;
  label: string;
  items: NavItem[];
};

const NAV_GROUP_DEFS: ReadonlyArray<{ id: string; label: string; context: NavContext }> = [
  { id: 'workspace', label: 'Workspace', context: 'workspace' },
  { id: 'platform', label: 'Platform', context: 'platform' },
  { id: 'account', label: 'Account', context: 'account' },
];

function IconAccount({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
    </svg>
  );
}

export const layoutNavItems: NavItem[] = [
  // Workspace
  {
    id: 'workspace-dashboard',
    href: '/',
    label: 'Dashboard',
    end: true,
    context: 'workspace',
    Icon: IconDashboard,
  },
  {
    id: 'workspace-quick-setup',
    href: '/setup',
    label: 'Quick setup',
    context: 'workspace',
    Icon: IconSearch,
    highlight: true,
    capability: 'canViewQuickSetup',
    visible: (me) => canViewQuickSetup(me),
  },
  {
    id: 'workspace-searches',
    href: '/searches',
    label: 'Searches',
    context: 'workspace',
    Icon: IconSearch,
  },
  {
    id: 'workspace-recipients',
    href: '/recipients',
    label: 'Recipients',
    context: 'workspace',
    Icon: IconUsers,
    capability: 'canViewRecipientsNav',
    visible: (me) => canViewRecipientsNav(me),
  },
  {
    id: 'workspace-subscriptions',
    href: '/subscriptions',
    label: 'Subscriptions',
    context: 'workspace',
    Icon: IconLink,
    capability: 'canViewSubscriptions',
    visible: (me) => canViewSubscriptions(me),
  },
  {
    id: 'workspace-templates',
    href: '/templates',
    label: 'Templates',
    context: 'workspace',
    Icon: IconLink,
    featureFlag: 'marketplace',
    capability: 'canBrowseTemplates',
    visible: (me) => canBrowseTemplates(me),
  },
  {
    id: 'workspace-activity',
    href: '/customer-activity',
    label: 'Activity',
    context: 'workspace',
    Icon: IconActivity,
    capability: 'canViewWorkspaceActivity',
    visible: (me) => canViewWorkspaceActivity(me),
  },
  {
    id: 'workspace-jobs',
    href: '/jobs',
    label: 'Jobs',
    context: 'workspace',
    Icon: IconJobs,
    capability: 'canManageJobs',
    visible: (me) => me?.permissions.canManageJobs ?? false,
  },
  {
    id: 'workspace-operations',
    href: '/activity',
    label: 'Operations',
    context: 'workspace',
    Icon: IconActivity,
    capability: 'canViewGlobalActivity',
    visible: (me) => canViewPlatformActivity(me),
  },
  {
    id: 'workspace-pipeline',
    href: '/pipeline',
    label: 'Pipeline',
    context: 'workspace',
    Icon: IconActivity,
    capability: 'canViewDiagnostics',
    visible: (me) => me?.permissions.canViewDiagnostics ?? false,
  },
  {
    id: 'workspace-release-health',
    href: '/release-health',
    label: 'Release health',
    context: 'workspace',
    Icon: IconDashboard,
    capability: 'canViewDiagnostics',
    visible: (me) => me?.permissions.canViewDiagnostics ?? false,
  },
  {
    id: 'workspace-usage',
    href: '/usage',
    label: 'Usage',
    context: 'workspace',
    Icon: IconDashboard,
    capability: 'canViewWorkspaceUsage',
    visible: (me) => canViewWorkspaceUsage(me),
  },
  {
    id: 'workspace-users',
    href: '/users',
    label: 'Users',
    context: 'workspace',
    Icon: IconUsers,
    capability: 'canViewWorkspaceUsers',
    visible: (me) => canViewWorkspaceUsers(me),
  },
  {
    id: 'workspace-billing',
    href: '/billing',
    label: 'Billing',
    context: 'workspace',
    Icon: IconSettings,
    featureFlag: 'customer_billing',
    capability: 'canViewCustomerBillingNav',
    visible: (me) => canViewCustomerBillingNav(me),
  },
  {
    id: 'workspace-overview',
    href: '/workspace',
    label: 'Workspace overview',
    context: 'workspace',
    Icon: IconUsers,
    capability: 'canViewWorkspaceNav',
    visible: (me) => canViewWorkspaceNav(me),
  },

  // Platform
  {
    id: 'platform-organizations',
    href: '/organizations/manage',
    label: 'Organizations',
    context: 'platform',
    Icon: IconUsers,
    capability: 'canManagePlatformUsers',
    visible: (me) =>
      Boolean(me?.permissions.canManagePlatformUsers ?? me?.permissions.canManageUsers) &&
      Boolean(me?.permissions.canSwitchWorkspace),
  },
  {
    id: 'platform-users',
    href: '/admin/platform-users',
    label: 'Platform users',
    context: 'platform',
    Icon: IconUsers,
    capability: 'canManageUsers',
    visible: (me) => me?.permissions.canManageUsers ?? false,
  },
  {
    id: 'platform-admin-dashboard',
    href: '/admin/billing',
    label: 'Dashboard',
    context: 'platform',
    Icon: IconDashboard,
    capability: 'canViewAdminBilling',
    visible: (me) => me?.permissions.canViewAdminBilling ?? false,
  },
  {
    id: 'platform-billing-subscriptions',
    href: '/admin/billing/subscriptions',
    label: 'Billing',
    context: 'platform',
    Icon: IconSettings,
    capability: 'canViewAdminBilling',
    visible: (me) => me?.permissions.canViewAdminBilling ?? false,
  },
  {
    id: 'platform-plans',
    href: '/admin/billing/plans',
    label: 'Plans',
    context: 'platform',
    Icon: IconSettings,
    capability: 'canManageAdminBilling',
    visible: (me) => me?.permissions.canManageAdminBilling ?? false,
  },
  {
    id: 'platform-payments',
    href: '/admin/billing/payments',
    label: 'Payments',
    context: 'platform',
    Icon: IconSettings,
    capability: 'canViewAdminBilling',
    visible: (me) => me?.permissions.canViewAdminBilling ?? false,
  },
  {
    id: 'platform-providers',
    href: '/admin/billing/providers',
    label: 'Providers',
    context: 'platform',
    Icon: IconSettings,
    capability: 'canManagePaymentProviderConfig',
    visible: (me) => me?.permissions.canManagePaymentProviderConfig ?? false,
  },
  {
    id: 'platform-approvals',
    href: '/approvals',
    label: 'Approvals',
    context: 'platform',
    Icon: IconUsers,
    capability: 'canApproveUsers',
    visible: (me) => me?.permissions.canApproveUsers ?? false,
  },
  {
    id: 'platform-sources',
    href: '/sources',
    label: 'Sources',
    context: 'platform',
    Icon: IconGlobe,
    capability: 'canManageSources',
    visible: (me) => me?.permissions.canManageSources ?? false,
  },
  {
    id: 'platform-config',
    href: '/config',
    label: 'Config',
    context: 'platform',
    Icon: IconSettings,
    capability: 'canManageConfig',
    visible: (me) => me?.permissions.canManageConfig ?? false,
  },
  {
    id: 'platform-support-view',
    href: '/support',
    label: 'Support view',
    context: 'platform',
    Icon: IconSettings,
    capability: 'canOpenSupportView',
    visible: (me) => me?.permissions.canOpenSupportView ?? false,
  },
  {
    id: 'platform-maintenance',
    href: '/maintenance',
    label: 'Maintenance',
    context: 'platform',
    Icon: IconSettings,
    featureFlag: 'maintenance',
    capability: 'canManageJobs',
    visible: (me) => me?.permissions.canManageJobs ?? false,
  },
  {
    id: 'platform-entitlements',
    href: '/billing/subscriptions',
    label: 'Entitlements',
    context: 'platform',
    Icon: IconSettings,
    capability: 'canManageBilling',
    visible: (me) =>
      (me?.permissions.canManageBilling ?? false) &&
      !(me?.permissions.canViewAdminBilling ?? false),
  },
  {
    id: 'platform-billing-attention',
    href: '/billing/attention',
    label: 'Billing attention',
    context: 'platform',
    Icon: IconSettings,
    visible: (me) =>
      (me?.permissions.canManageBilling ?? false) &&
      me?.role === 'owner' &&
      !(me?.permissions.canViewAdminBilling ?? false),
  },
  {
    id: 'platform-payment-providers',
    href: '/payment-providers',
    label: 'Payment providers',
    context: 'platform',
    Icon: IconSettings,
    visible: (me) =>
      (me?.permissions.canManageBilling ?? false) &&
      (me?.role === 'owner' || me?.role === 'platform_admin' || me?.role === 'admin') &&
      !(me?.permissions.canViewAdminBilling ?? false),
  },

  // Account
  {
    id: 'account-my-account',
    href: '/account',
    label: 'My account',
    context: 'account',
    Icon: IconAccount,
  },
];

export function filterVisibleNav(
  items: NavItem[],
  me: MeProfile | null,
  flags?: FeatureFlagsMap,
): NavItem[] {
  return items.filter((item) => {
    if (item.featureFlag && flags && !flags[item.featureFlag]) {
      return false;
    }
    return !item.visible || item.visible(me, flags ?? ({} as FeatureFlagsMap));
  });
}

export function buildVisibleNavGroups(
  items: NavItem[],
  me: MeProfile | null,
  flags?: FeatureFlagsMap,
): NavGroup[] {
  const visible = filterVisibleNav(items, me, flags);
  return NAV_GROUP_DEFS.map((def) => ({
    id: def.id,
    label: def.label,
    items: visible.filter((item) => item.context === def.context),
  })).filter((group) => group.items.length > 0);
}

/** @deprecated Use buildVisibleNavGroups */
export function groupVisibleNav(
  items: NavItem[],
  me: MeProfile | null,
  flags?: FeatureFlagsMap,
): Record<NavSection, NavItem[]> {
  const groups = buildVisibleNavGroups(items, me, flags);
  const platform = groups.find((group) => group.id === 'platform')?.items ?? [];
  const workspace = groups.find((group) => group.id === 'workspace')?.items ?? [];
  const account = groups.find((group) => group.id === 'account')?.items ?? [];
  return {
    administration: platform,
    workspace: [...workspace, ...account],
  };
}
